import { HEX_212F41, HEX_F2F6FA } from '@tanium/colors';
import { Box } from '@tanium/react-box';
import { useTheme } from '@tanium/react-theme-context';
import { defineColor } from '@tanium/react-theme-definition';
import { ColorsValue, ThemeChartColors } from '@tanium/theme-data';
import { useFormikContext } from 'formik';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Label } from '@tanium/react-label';
import { CreateRequest } from 'gql/Request';
import RequestSlice from 'components/pages/request/store/RequestSlice';
import RequestSelectors from 'components/pages/request/store/RequestSelectors';
import { CreateRequestForm } from 'components/pages/request/CreateRequestPage';
import IApprovalLevel from 'common/IApprovalLevel';

const EmptyStateBackground = defineColor([HEX_F2F6FA, HEX_212F41]);
export const AUTO_APPROVE = 'autoApprove';

export const getAccessLevelColor = (accessLevel: number): ColorsValue => {
  switch (accessLevel) {
    case IApprovalLevel.AutoApprovalLevel:
      return ThemeChartColors.StatusOptimized; // dark green
    case IApprovalLevel.OtherLevel:
      return ThemeChartColors.StatusAboveAverage; // light green
    case IApprovalLevel.TeamLevel:
      return ThemeChartColors.StatusAverage; // yellow
    case IApprovalLevel.OrgLevel:
      return ThemeChartColors.StatusBelowAverage; // orange
    case IApprovalLevel.ManagerLevel:
    case IApprovalLevel.ExecLevel:
    default:
      return ThemeChartColors.StatusNeedsImprovement; // dark red
  }
};

const AccessLevelConfigDisplay: React.VFC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { values } = useFormikContext<CreateRequestForm>();
  const prevAccessLevelInput = useRef('');
  const formik = useFormikContext<CreateRequestForm>();
  const requestAccessLevelConfig = useSelector(RequestSelectors.selectRequestAccessLevelConfig);
  const accessLevels = useSelector(RequestSelectors.selectAccessLevels);
  const requestAccessLevel = accessLevels.find(
    (access) => access.accessLevel === requestAccessLevelConfig?.accessLevel
  );

  useEffect(() => {
    if (isFormComplete(values)) {
      const accessLevelInput = getAccessLevelFieldsInput(values);

      if (accessLevelInput !== prevAccessLevelInput.current) {
        const createRequestInput: CreateRequest = {
          targetType: values.targetType,
          targetId: values.targetId,
          access: values.accessTypes,
          ticketInfo: '',
          details: '',
          autopage: values.autopage,
        };

        prevAccessLevelInput.current = accessLevelInput;
        dispatch(RequestSlice.actions.calculateRequestAccessLevelConfig(createRequestInput));
      }
    } else if (requestAccessLevelConfig) {
      prevAccessLevelInput.current = '';
      dispatch(RequestSlice.actions.clearRequestAccessLevelConfig());
    }
  }, [values, requestAccessLevelConfig, dispatch]);

  useEffect(() => {
    if (requestAccessLevel) {
      void formik.setFieldValue(AUTO_APPROVE, requestAccessLevel.accessLevel === 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestAccessLevel, values]);

  if (!requestAccessLevel) {
    return null;
  }

  return (
    <Box backgroundColor={EmptyStateBackground(theme)} p={'12px'} display="flex" justifyContent="center">
      <Label
        color={getAccessLevelColor(requestAccessLevel.accessLevel)}
        keyForValue={t('APPROVAL_NEEDED')}
        maxWidth="100%"
        customTooltipContent={
          <Box pl={2} pr={2} pb={1}>
            <p>{t('ESCALATION_LEVEL_DESCRIPTION')}</p>
            {accessLevels
              .sort((a, b) => b.accessLevel - a.accessLevel)
              .map((level, i) => {
                return (
                  <Box
                    key={i}
                    color={getAccessLevelColor(level.accessLevel)}
                  >{`${level.accessLevel} - ${level.name}`}</Box>
                );
              })}
          </Box>
        }
      >
        <strong>{requestAccessLevel.name}</strong>
      </Label>
    </Box>
  );
};

const isFormComplete = (values: CreateRequestForm): boolean => {
  return !!(values.accessTypes && values.accessTypes.length && values.targetType && values.targetId);
};

const getAccessLevelFieldsInput = (values: CreateRequestForm): string => {
  return JSON.stringify({
    targetType: values.targetType,
    targetId: values.targetId,
    accessTypes: values.accessTypes,
  });
};

export default AccessLevelConfigDisplay;
